/**
 * Tag Rules API Hooks for Text, Gateway, Path, Lander, Offer variants.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

import type {
  ListTagRule,
  CreateListTagRuleDto,
  UpdateListTagRuleDto,
  ListTagRulesSettings,
  UpdateListTagRulesSettingsPayload,
} from './useListTagRules';

export type TagRuleVariant =
  | 'text'
  | 'gateway'
  | 'path'
  | 'lander'
  | 'offer'
  | 'phone'
  | 'drop'
  | 'source'
  | 'hour';

export type TagRuleTabVariant = TagRuleVariant | 'list';

const VARIANT_PATHS: Record<TagRuleVariant, string> = {
  text: 'text-tag-rules',
  gateway: 'gateway-tag-rules',
  path: 'path-tag-rules',
  lander: 'lander-tag-rules',
  offer: 'offer-tag-rules',
  phone: 'phone-tag-rules',
  drop: 'drop-tag-rules',
  source: 'source-tag-rules',
  hour: 'hour-tag-rules',
};

function tagRulesKeys(variant: TagRuleVariant) {
  const base = ['smswiser', VARIANT_PATHS[variant]] as const;
  return {
    all: () => base,
    settings: () => [...base, 'settings'] as const,
  };
}

export function useTagRulesSettings(variant: TagRuleVariant) {
  const path = VARIANT_PATHS[variant];
  return useQuery({
    queryKey: tagRulesKeys(variant).settings(),
    queryFn: async (): Promise<ListTagRulesSettings> => {
      const res = await api.get<{
        data?: {
          newDays?: number;
          newColor?: string;
          unusedColor?: string;
          unusedSinceIso?: string;
          cacheEnabled?: boolean;
          cacheTtlHours?: number;
        };
      }>(`/smswiser/${path}/settings`);
      const data =
        res.data?.data ??
        (res.data as
          | {
              newDays?: number;
              newColor?: string;
              unusedColor?: string;
              unusedSinceIso?: string;
              cacheEnabled?: boolean;
              cacheTtlHours?: number;
            }
          | undefined);
      return {
        newDays: Number(data?.newDays) || 2,
        newColor: data?.newColor ?? '#3b82f6',
        unusedColor: data?.unusedColor ?? '#6b7280',
        unusedSinceIso: String(data?.unusedSinceIso ?? '').trim(),
        cacheEnabled: data?.cacheEnabled ?? true,
        cacheTtlHours: Math.max(1, Math.min(168, Number(data?.cacheTtlHours) || 12)),
      };
    },
  });
}

export function useUpdateTagRulesSettings(variant: TagRuleVariant) {
  const queryClient = useQueryClient();
  const path = VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (
      payload: UpdateListTagRulesSettingsPayload,
    ): Promise<ListTagRulesSettings> => {
      const res = await api.patch<{
        data?: {
          newDays?: number;
          newColor?: string;
          unusedColor?: string;
          unusedSinceIso?: string;
          cacheEnabled?: boolean;
          cacheTtlHours?: number;
        };
      }>(`/smswiser/${path}/settings`, payload);
      const data =
        res.data?.data ??
        (res.data as
          | {
              newDays?: number;
              newColor?: string;
              unusedColor?: string;
              unusedSinceIso?: string;
              cacheEnabled?: boolean;
              cacheTtlHours?: number;
            }
          | undefined);
      return {
        newDays: Number(data?.newDays) || 2,
        newColor: data?.newColor ?? '#3b82f6',
        unusedColor: data?.unusedColor ?? '#6b7280',
        unusedSinceIso: String(data?.unusedSinceIso ?? '').trim(),
        cacheEnabled: data?.cacheEnabled ?? true,
        cacheTtlHours: Math.max(1, Math.min(168, Number(data?.cacheTtlHours) || 12)),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagRulesKeys(variant).settings() });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useTagRules(variant: TagRuleVariant) {
  const path = VARIANT_PATHS[variant];
  return useQuery({
    queryKey: tagRulesKeys(variant).all(),
    queryFn: async () => {
      const res = await api.get<{ data: ListTagRule[] }>(`/smswiser/${path}`);
      const data = res.data?.data;
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useCreateTagRule(variant: TagRuleVariant) {
  const queryClient = useQueryClient();
  const path = VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (dto: CreateListTagRuleDto) => {
      const res = await api.post<{ data: ListTagRule }>(`/smswiser/${path}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagRulesKeys(variant).all() });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useBulkCreateTagRules(variant: TagRuleVariant) {
  const queryClient = useQueryClient();
  const path = VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (rules: CreateListTagRuleDto[]) => {
      const res = await api.post<{ data: ListTagRule[]; created: number }>(
        `/smswiser/${path}/bulk`,
        { rules },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagRulesKeys(variant).all() });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useUpdateTagRule(variant: TagRuleVariant) {
  const queryClient = useQueryClient();
  const path = VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateListTagRuleDto }) => {
      const res = await api.patch<{ data: ListTagRule }>(
        `/smswiser/${path}/${id}`,
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagRulesKeys(variant).all() });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useDeleteTagRule(variant: TagRuleVariant) {
  const queryClient = useQueryClient();
  const path = VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/smswiser/${path}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagRulesKeys(variant).all() });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

const TAB_VARIANT_PATHS: Record<TagRuleTabVariant, string> = {
  list: 'list-tag-rules',
  text: 'text-tag-rules',
  gateway: 'gateway-tag-rules',
  path: 'path-tag-rules',
  lander: 'lander-tag-rules',
  offer: 'offer-tag-rules',
  phone: 'phone-tag-rules',
  drop: 'drop-tag-rules',
  source: 'source-tag-rules',
  hour: 'hour-tag-rules',
};

const TAG_RULES_TAB_KEYS = ['smswiser', 'tag-rules'] as const;

function invalidateListPerformanceTags(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey.includes('list-performance-tags'),
  });
}

function invalidatePerformanceTagsForVariant(
  queryClient: ReturnType<typeof useQueryClient>,
  variant: TagRuleTabVariant,
) {
  if (variant === 'list') {
    invalidateListPerformanceTags(queryClient);
    return;
  }
  const perfKey = `${variant}-performance-tags`;
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey.includes(perfKey),
  });
}

export function useTagRulesForTab(variant: TagRuleTabVariant) {
  const path = TAB_VARIANT_PATHS[variant];
  return useQuery({
    queryKey: [...TAG_RULES_TAB_KEYS, variant],
    queryFn: async () => {
      const res = await api.get<{ data: ListTagRule[] }>(`/smswiser/${path}`);
      const data = res.data?.data;
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useCreateTagRuleForTab(variant: TagRuleTabVariant) {
  const queryClient = useQueryClient();
  const path = TAB_VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (dto: CreateListTagRuleDto) => {
      const res = await api.post<{ data: ListTagRule }>(`/smswiser/${path}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TAG_RULES_TAB_KEYS, variant] });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useBulkCreateTagRulesForTab(variant: TagRuleTabVariant) {
  const queryClient = useQueryClient();
  const path = TAB_VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (rules: CreateListTagRuleDto[]) => {
      const res = await api.post<{ data: ListTagRule[]; created: number }>(
        `/smswiser/${path}/bulk`,
        { rules },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TAG_RULES_TAB_KEYS, variant] });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useUpdateTagRuleForTab(variant: TagRuleTabVariant) {
  const queryClient = useQueryClient();
  const path = TAB_VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateListTagRuleDto }) => {
      const res = await api.patch<{ data: ListTagRule }>(`/smswiser/${path}/${id}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TAG_RULES_TAB_KEYS, variant] });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useDeleteTagRuleForTab(variant: TagRuleTabVariant) {
  const queryClient = useQueryClient();
  const path = TAB_VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/smswiser/${path}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TAG_RULES_TAB_KEYS, variant] });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useBulkDeleteTagRulesForTab(variant: TagRuleTabVariant) {
  const queryClient = useQueryClient();
  const path = TAB_VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.delete<{ deleted: number }>(`/smswiser/${path}/bulk`, {
        data: { ids },
      });
      return res.data?.deleted ?? 0;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TAG_RULES_TAB_KEYS, variant] });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useReorderTagRulesForTab(variant: TagRuleTabVariant) {
  const queryClient = useQueryClient();
  const path = TAB_VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (updates: Array<{ id: string; orderIndex: number }>) => {
      const res = await api.patch<{ data: { updated: number } }>(
        `/smswiser/${path}/reorder`,
        { updates },
      );
      return res.data?.data ?? { updated: 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TAG_RULES_TAB_KEYS, variant] });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}

export function useReorderTagRules(variant: TagRuleVariant) {
  const queryClient = useQueryClient();
  const path = VARIANT_PATHS[variant];
  return useMutation({
    mutationFn: async (updates: Array<{ id: string; orderIndex: number }>) => {
      const res = await api.patch<{ data: { updated: number } }>(
        `/smswiser/${path}/reorder`,
        { updates },
      );
      return res.data?.data ?? { updated: 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagRulesKeys(variant).all() });
      invalidatePerformanceTagsForVariant(queryClient, variant);
    },
  });
}
